import { get, isEmpty, sortBy } from 'lodash';

import {
  DecimalNumber,
  decimalNumber,
} from '@app/shared/common/decimal-number/decimal-number';
import {
  ENTITY_TYPES,
  IEmployee,
  IProduct,
  PermissionValue,
  RequireOnly,
  GridRowData,
  ListColumn,
  EPlacement,
} from '@app/shared/types';
import { DEFAULT_LOAD } from '@config/global-constants';
import { IBaseEntityType } from '@app/shared/types/entities/base/base.type';
import { HAWKER_SESSION_STATUS_VALUE } from '@app/shared/types/entities/hawker-session/hawker-session-status.enum';
import { toMoment } from '@shared/utils';
import { PermissionService } from '../session/permission.service';
import { generateDecimalRegexp } from '@app/shared/constants';

export const calcQtyRequested = (product: IProduct): DecimalNumber => {
  const loadRequested = get(product, 'load_requested', 0) ?? 0;
  const load = get(product, 'load', DEFAULT_LOAD);

  return decimalNumber(loadRequested).mul(load);
};

export const calcCloseOutPreviewFields = (item: any): Partial<GridRowData> => {
  const totalRequested = get(item, 'total_requested', 0);
  const totalReturned = get(item, 'total_returned', 0);
  const terminalQty = get(item, 'terminal_quantity', 0);
  const netQtyTaken = decimalNumber(totalRequested).sub(totalReturned);

  return {
    total_requested: totalRequested,
    total_returned: totalReturned,
    net_qty_taken: netQtyTaken.valueOf(),
    variance_qty: netQtyTaken.sub(terminalQty).valueOf(),
  };
};

export const formatEmployeeName = (
  employee: RequireOnly<IEmployee, 'Firstname' | 'Lastname'>,
  options?: {
    showBadgeNumber?: boolean;
  },
): string => {
  const firstName = get(employee, 'Firstname', '');
  const lastName = get(employee, 'Lastname', '');
  const badgeNumber = get(employee, 'BadgeNumber', '');
  let fullName = `${lastName}, ${firstName}`;

  const showBadgeNumber = options?.showBadgeNumber ?? false;

  fullName =
    !showBadgeNumber || isEmpty(badgeNumber)
      ? fullName
      : `${fullName} (${badgeNumber})`;

  return fullName.replace(/\s+/g, ' ').trim();
};

/**
 * Defines the display order for hawker session statuses.
 * Order matters: earlier positions appear first in the list.
 */
const SESSION_STATUS_SORT_ORDER: string[] = [
  HAWKER_SESSION_STATUS_VALUE.Open,
  HAWKER_SESSION_STATUS_VALUE.Closed,
  HAWKER_SESSION_STATUS_VALUE.Canceled,
];

/**
 * Sort hawker sessions by status group (Open → Closed → Canceled),
 * then by created_at descending within each group.
 */
export const sortHawkerSessions = <T extends IBaseEntityType>(
  sessions: T[],
): T[] =>
  sortBy(sessions, [
    (session) => {
      const status = get(session, 'status');
      const index = SESSION_STATUS_SORT_ORDER.indexOf(status);

      return index === -1 ? SESSION_STATUS_SORT_ORDER.length : index;
    },
    (session) => {
      const createdAt = get(session, 'created_at');

      if (!createdAt) {
        return 0;
      }

      const timestamp = toMoment(createdAt).valueOf();

      return isNaN(timestamp) ? 0 : -timestamp;
    },
  ]);

export const canAddPermission = (
  _: ENTITY_TYPES,
  permissionService: PermissionService,
) =>
  permissionService.hasPermissions(
    `${ENTITY_TYPES.HAWKER_CONFIG}.${PermissionValue.Edit}`,
  );

export const loadOrReturnedInputParams = (
  options = {},
): ListColumn['cellRendererParams'] => {
  return {
    defaultValue: null,
    label: null,
    precision: 0,
    usePreferenceDecimalPrecision: {
      overrideMin: false,
      overrideMax: true,
    },
    blurWhenToNext: false,
    compareChangeWithValueBeforeEdit: true,
    disableKeyUpFinishEditing: true,
    allowEmptyValue: true,
    acceptableInputRegex: generateDecimalRegexp(0, true, { allowZero: true }),
    ...options,
  };
};

export const getColumnTipConfig = (description: string): any => ({
  hintOptions: {
    description,
    placement: EPlacement.top,
  },
});
